import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import Select from "react-select";
import { useCompanies } from "../../../../hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../../components/PageHero";
import FilterBand from "../../../../components/FilterBand";
import TablePagination from "../../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import ShellCard from "../../../../components/ShellCard";
import MetricCard from "../../../../components/MetricCard";
import MetricCardGroup from "../../../../components/MetricCardGroup";
import { reactSelectStyles, reactSelectPortalProps } from "../../../../utils/reactSelectStyles";
import {
  listCatalogoTransportadoras,
  vincularTransportadoras,
  importarPlanilhaTransportadoras,
  baixarModeloPlanilhaTransportadoras,
} from "../../../../services/catalogoTransportadoraApi";
import DetalheTransportadoraModal from "../DetalheTransportadoraModal";
import CatalogoCard from "./CatalogoCard";
import { FASE_ROTULO } from "../MinhasTransportadoras/esteiraEstado";
// PO1-3509: e-mail de suporte único do Hub.
import { SUPORTE_EMAIL } from "../../../../constants/suporte";
import { UF_OPTIONS } from "../../../../constants/ufs";
// PO1-3856/PO1-3857: filtros de categoria, sistema, modalidade, sede e rota.
import {
  CATEGORIA_OPTIONS,
  SISTEMA_OPTIONS,
  MODALIDADE_OPTIONS,
  REGIAO_OPTIONS,
  FILTROS_VAZIOS,
  contarFiltros,
  montarParametros,
} from "./filtros";

// Catálogo global de transportadoras (H2, PO1-2620) — grid de cards paginado
// (não tabela, decisão de produto) + seleção múltipla + import por planilha +
// CTA "fora do catálogo". PO1-2727: padrão visual do Hub (PageHero, FilterBand
// server, TablePagination, modal tematizado, zero cor cravada).
// 60 é múltiplo de 1 a 6: o grid (auto-fill, 260px) fecha todas as linhas da
// página em qualquer largura. Com 24 e 5 colunas, a última linha ficava com
// um buraco e parecia fim de lista, escondendo as próximas páginas.
const PAGE_SIZE = 60;

// PO1-3317: capacidade virou filtro em cards com contagem (antes era o checkbox
// "Só com cotação disponível" dentro do painel). `valor` é o sufixo do cap_*.
const CARDS_CAPACIDADE = [
  { key: "todas", valor: "", label: "Todas", helper: "Transportadoras no recorte", icon: "tim-icons icon-bullet-list-67", tone: "neutral" },
  { key: "cotacao", valor: "cotacao", label: "Cotação disponível", helper: "Cotam por API ou SSW", icon: "tim-icons icon-money-coins", tone: "info" },
  { key: "tracking", valor: "tracking", label: "Rastreio", helper: "Rastreamento pela integração", icon: "tim-icons icon-square-pin", tone: "success" },
  { key: "coleta", valor: "coleta", label: "Coleta", helper: "Agendamento de coleta pela integração", icon: "tim-icons icon-delivery-fast", tone: "warning" },
  // PO1-2570: envio e etiqueta a partir da NF (Correios, Melhor Envio, Mandaê, Loggi).
  { key: "etiqueta", valor: "etiqueta", label: "Etiqueta", helper: "Envio e etiqueta pela integração", icon: "tim-icons icon-tag", tone: "info" },
];

// PO1-3317: card maior (logo em faixa), mesma largura mínima do catálogo de conectores.
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: "0.85rem",
};

const fieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.76rem",
  marginBottom: "0.35rem",
};

const stateBoxStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.86rem",
  padding: "2rem 1rem",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.6rem",
};

// PO1-3856: toggle "Fora da minha carteira" na faixa de busca (padrão do
// .recalc-check de Recalcular, inline porque aquele CSS é da página).
const toggleLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.45rem",
  color: AUTOPILOT_COLORS.text,
  fontSize: "0.82rem",
  fontWeight: 500,
  cursor: "pointer",
  userSelect: "none",
  margin: 0,
  whiteSpace: "nowrap",
};

// PO1-3857: divisor do grid antes do primeiro item sem cobertura informada na rota.
const divisorSemCoberturaStyle = {
  gridColumn: "1 / -1",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  paddingTop: "0.9rem",
  marginTop: "0.35rem",
  borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
};

const coberturaLinhaStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.8rem",
  marginTop: "-0.35rem",
  marginBottom: "0.85rem",
};

// Opções de react-select (isMulti) a partir dos valores escolhidos.
const opcoesSelecionadas = (options, valores) => options.filter((o) => (valores || []).indexOf(o.value) !== -1);
const valoresDe = (opts) => (Array.isArray(opts) ? opts.map((o) => o.value) : []);

// PO1-3283: vincular/importar passam pela porta única e devolvem o estado da
// esteira por item (`jornada.itens[]` ou `itens[]`) e a contagem `curadoria`.
// Resume para o toast: um estado só quando todas caíram no mesmo.
const resumoJornada = (resultado) => {
  const itens = (resultado && ((resultado.jornada && resultado.jornada.itens) || resultado.itens)) || [];
  const estados = itens.map((i) => i && i.estado).filter((e) => e && e !== "EM_CURADORIA" && e !== "ERRO");
  const unicos = [...new Set(estados)];
  const estadoUnico = unicos.length === 1 ? FASE_ROTULO[unicos[0]] || unicos[0] : null;
  const contagem = unicos.length > 1 ? unicos.map((e) => `${estados.filter((x) => x === e).length} ${FASE_ROTULO[e] || e}`).join(", ") : null;
  return {
    curadoria: Number(resultado && resultado.curadoria) || itens.filter((i) => i && i.estado === "EM_CURADORIA").length,
    estadoUnico,
    descricao: estadoUnico ? `Esteira: ${estadoUnico}.` : contagem ? `Esteira: ${contagem}.` : null,
  };
};

export default function Catalogo() {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const embarcadorId = empresa?.value || null;

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const listaRef = useRef(null);
  // Busca: `buscaInput` é o campo (controlado); `search` é o termo aplicado
  // (o FilterBand em modo server dispara onSearchSubmit com debounce/Enter).
  const [buscaInput, setBuscaInput] = useState("");
  const [search, setSearch] = useState("");
  // Filtros avançados: rascunho no painel, aplicados em "Aplicar filtros".
  const [filtrosDraft, setFiltrosDraft] = useState(FILTROS_VAZIOS);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  // PO1-3856: "Fora da minha carteira" aplica na hora (não espera "Aplicar").
  const [foraCarteira, setForaCarteira] = useState(false);
  // PO1-3317: capacidade escolhida nos cards ("" = todas) e contagem de cada uma.
  const [capacidade, setCapacidade] = useState("");
  const [contagens, setContagens] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [adicionandoIds, setAdicionandoIds] = useState(() => new Set());
  const [vinculandoLote, setVinculandoLote] = useState(false);
  const [selectedDetalheId, setSelectedDetalheId] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);

  const carregar = useCallback(async () => {
    if (!embarcadorId) return;
    setLoading(true);
    setErro("");
    try {
      const data = await listCatalogoTransportadoras({
        embarcadorId,
        search: search || undefined,
        capacidade: capacidade ? `cap_${capacidade}` : undefined,
        ...montarParametros({ filtros, foraCarteira }),
        page,
        pageSize: PAGE_SIZE,
      });
      setItems((data && data.items) || []);
      setTotal((data && data.total) || 0);
      setContagens((data && data.contagens) || null);
    } catch (e) {
      const mensagem = e?.response?.data?.error || "Falha ao carregar o catálogo.";
      setErro(mensagem);
      addToast({ place: "tr", type: "danger", title: "Erro", description: mensagem });
    } finally {
      setLoading(false);
    }
  }, [embarcadorId, search, filtros, foraCarteira, capacidade, page, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Trocar busca/filtro volta pra página 1 — não reseta a seleção (o usuário
  // pode estar selecionando itens de páginas/filtros diferentes antes de
  // adicionar todos de uma vez).
  useEffect(() => {
    setPage(1);
  }, [search, filtros, foraCarteira, capacidade]);

  const filtrosAtivosCount = contarFiltros(filtros) + (foraCarteira ? 1 : 0);

  const aplicarFiltros = () => setFiltros(filtrosDraft);
  const limparFiltros = () => {
    setFiltrosDraft(FILTROS_VAZIOS);
    setFiltros(FILTROS_VAZIOS);
    setForaCarteira(false);
    setCapacidade("");
    setBuscaInput("");
    setSearch("");
  };

  const toggleSelecionado = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const adicionarUma = async (id) => {
    if (!embarcadorId) return;
    setAdicionandoIds((prev) => new Set(prev).add(id));
    try {
      const resultado = await vincularTransportadoras(embarcadorId, [id]);
      const r = resumoJornada(resultado);
      addToast({
        place: "tr",
        type: r.curadoria ? "warning" : "success",
        title: r.curadoria ? "Aguardando a Everlog" : `Adicionada${r.estadoUnico ? `: ${r.estadoUnico}` : ""}`,
        description: r.curadoria
          ? "A matriz desta transportadora precisa de ajuste pela Everlog; o vínculo nasce quando ela liberar."
          : r.descricao || "Transportadora adicionada à sua carteira.",
      });
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: e?.response?.data?.error || "Falha ao adicionar." });
    } finally {
      setAdicionandoIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const adicionarSelecionadas = async () => {
    if (!embarcadorId || !selectedIds.size) return;
    setVinculandoLote(true);
    try {
      const resultado = await vincularTransportadoras(embarcadorId, [...selectedIds]);
      const r = resumoJornada(resultado);
      addToast({
        place: "tr",
        type: r.curadoria ? "warning" : "success",
        title: "Adicionadas",
        description: [
          `${resultado.adicionadas} transportadora(s) adicionada(s) à sua carteira.`,
          r.descricao,
          r.curadoria ? `${r.curadoria} aguardando a Everlog (ajuste de matriz).` : null,
        ].filter(Boolean).join(" "),
      });
      setSelectedIds(new Set());
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: e?.response?.data?.error || "Falha ao adicionar em lote." });
    } finally {
      setVinculandoLote(false);
    }
  };

  const baixarModelo = async () => {
    try {
      const blob = await baixarModeloPlanilhaTransportadoras();
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "modelo-transportadoras.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: "Falha ao baixar o modelo." });
    }
  };

  const importar = async () => {
    if (!embarcadorId || !importFile) return;
    setImportando(true);
    setResultadoImport(null);
    try {
      const resultado = await importarPlanilhaTransportadoras(embarcadorId, importFile);
      setResultadoImport(resultado);
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: e?.response?.data?.error || "Falha ao importar a planilha." });
    } finally {
      setImportando(false);
    }
  };

  const fecharImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setResultadoImport(null);
  };

  const mailtoSolicitacao = useMemo(() => {
    const subject = encodeURIComponent("Solicitação de nova transportadora no catálogo");
    return `mailto:${SUPORTE_EMAIL}?subject=${subject}`;
  }, []);

  if (!hasEmpresaSelection) {
    return (
      <div className="content catalogo-transportadoras-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para navegar no catálogo." />
      </div>
    );
  }

  const temFiltroOuBusca = Boolean(search) || filtrosAtivosCount > 0 || Boolean(capacidade);
  // PO1-3857: com rota aplicada a API devolve cobertura por item e nas contagens.
  const rotaAtiva = Boolean(filtros.ufOrigem || filtros.ufDestino);
  const contagemCobertura = rotaAtiva && contagens && contagens.cobertura ? contagens.cobertura : null;
  const indicePrimeiroSemCobertura = rotaAtiva
    ? items.findIndex((item) => item.cobertura && item.cobertura.nivel === "sem_informacao")
    : -1;
  const mostraSistema = filtrosDraft.categorias.indexOf("tms") !== -1;

  return (
    <div className="content catalogo-transportadoras-page">
      <PageHero
        eyebrow="Conexões"
        title="Catálogo de Transportadoras"
        description="Busque entre as transportadoras cadastradas na Everlog e adicione à sua carteira."
        actions={
          <>
            {selectedIds.size > 0 && (
              <Button className="btn-sm" color="info" disabled={vinculandoLote} onClick={adicionarSelecionadas}>
                {vinculandoLote ? <Spinner size="sm" /> : `${selectedIds.size} selecionada(s) · Adicionar todas`}
              </Button>
            )}
            <Button className="btn-sm" color="info" outline onClick={() => setImportModalOpen(true)}>
              Importar planilha
            </Button>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar transportadora"
        searchHint="Apelido, CNPJ ou razão social"
        searchPlaceholder="Digite o apelido, o CNPJ ou a razão social"
        searchValue={buscaInput}
        onSearchChange={setBuscaInput}
        onSearchSubmit={(valor) => setSearch(valor.trim())}
        activeFiltersCount={filtrosAtivosCount}
        open={filtrosOpen}
        onToggle={setFiltrosOpen}
        panelTitle="Filtros"
        panelDescription="Categoria, modalidade, sede e rota atendida."
        extra={
          <label style={toggleLabelStyle} htmlFor="catalogo-fora-carteira" title="Esconder as transportadoras que já estão na sua carteira">
            <input
              id="catalogo-fora-carteira"
              type="checkbox"
              checked={foraCarteira}
              onChange={(e) => setForaCarteira(e.target.checked)}
              style={{ margin: 0, accentColor: "var(--theme-accent)" }}
            />
            <span>Fora da minha carteira</span>
          </label>
        }
        onApply={aplicarFiltros}
        onClear={limparFiltros}
        busy={loading}
      >
        <Row>
          <Col md={mostraSistema ? 4 : 6}>
            <div style={fieldLabelStyle}>Categoria</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              isMulti
              placeholder="Todas as categorias"
              options={CATEGORIA_OPTIONS}
              value={opcoesSelecionadas(CATEGORIA_OPTIONS, filtrosDraft.categorias)}
              onChange={(opts) => {
                const categorias = valoresDe(opts);
                // Sistema só vale junto com "Pelo sistema da transportadora".
                setFiltrosDraft((prev) => ({
                  ...prev,
                  categorias,
                  sistemas: categorias.indexOf("tms") !== -1 ? prev.sistemas : [],
                }));
              }}
              isSearchable={false}
            />
          </Col>
          {mostraSistema && (
            <Col md={4}>
              <div style={fieldLabelStyle}>Sistema</div>
              <Select
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                isMulti
                placeholder="Todos os sistemas"
                options={SISTEMA_OPTIONS}
                value={opcoesSelecionadas(SISTEMA_OPTIONS, filtrosDraft.sistemas)}
                onChange={(opts) => setFiltrosDraft((prev) => ({ ...prev, sistemas: valoresDe(opts) }))}
                isSearchable={false}
              />
            </Col>
          )}
          <Col md={mostraSistema ? 4 : 6}>
            <div style={fieldLabelStyle}>Modalidade</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              isMulti
              placeholder="Todas as modalidades"
              options={MODALIDADE_OPTIONS}
              value={opcoesSelecionadas(MODALIDADE_OPTIONS, filtrosDraft.modalidades)}
              onChange={(opts) => setFiltrosDraft((prev) => ({ ...prev, modalidades: valoresDe(opts) }))}
              isSearchable={false}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: "0.85rem" }}>
          <Col md={4}>
            <div style={fieldLabelStyle}>Sede da matriz</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              aria-label="Sede da matriz"
              options={REGIAO_OPTIONS}
              value={REGIAO_OPTIONS.find((o) => o.value === filtrosDraft.regiao) || REGIAO_OPTIONS[0]}
              onChange={(opt) => setFiltrosDraft((prev) => ({ ...prev, regiao: (opt && opt.value) || "" }))}
              isSearchable={false}
            />
          </Col>
          <Col md={8}>
            <div style={fieldLabelStyle}>Atende a rota</div>
            <Row>
              <Col xs={6}>
                <Select
                  classNamePrefix="react-select"
                  styles={reactSelectStyles}
                  {...reactSelectPortalProps()}
                  aria-label="UF de origem"
                  placeholder="UF de origem"
                  options={UF_OPTIONS}
                  value={UF_OPTIONS.find((o) => o.value === filtrosDraft.ufOrigem) || UF_OPTIONS[0]}
                  formatOptionLabel={(o) => (o.value ? o.label : "Origem: todas")}
                  onChange={(opt) => setFiltrosDraft((prev) => ({ ...prev, ufOrigem: (opt && opt.value) || "" }))}
                  isSearchable
                />
              </Col>
              <Col xs={6}>
                <Select
                  classNamePrefix="react-select"
                  styles={reactSelectStyles}
                  {...reactSelectPortalProps()}
                  aria-label="UF de destino"
                  placeholder="UF de destino"
                  options={UF_OPTIONS}
                  value={UF_OPTIONS.find((o) => o.value === filtrosDraft.ufDestino) || UF_OPTIONS[0]}
                  formatOptionLabel={(o) => (o.value ? o.label : "Destino: todas")}
                  onChange={(opt) => setFiltrosDraft((prev) => ({ ...prev, ufDestino: (opt && opt.value) || "" }))}
                  isSearchable
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup
        title="O que a transportadora faz pela integração"
        description="Clique em um card para filtrar; clique de novo para voltar a todas."
        style={{ marginTop: "1.25rem" }}
      >
        {CARDS_CAPACIDADE.map((card) => (
          <MetricCard
            key={card.key}
            label={card.label}
            value={contagens ? contagens[card.key] ?? 0 : "—"}
            helper={card.helper}
            iconClassName={card.icon}
            tone={card.tone}
            active={capacidade === card.valor}
            onClick={() => setCapacidade(card.valor && capacidade === card.valor ? "" : card.valor)}
          />
        ))}
      </MetricCardGroup>

      <ShellCard
        title="Transportadoras no catálogo"
        description={
          loading
            ? "Carregando..."
            : `${total} transportadora(s)${temFiltroOuBusca ? " no recorte" : " cadastrada(s) na Everlog"}`
        }
        style={{ marginTop: "1.25rem" }}
      >
            <div ref={listaRef} />

            {!loading && contagemCobertura && (
              <div style={coberturaLinhaStyle}>
                {`Cobertura na rota: ${Number(contagemCobertura.comprovada) || 0} comprovadas · ${Number(contagemCobertura.declarada) || 0} declaradas · ${Number(contagemCobertura.semInformacao) || 0} sem informação`}
              </div>
            )}

            {loading ? (
              <div style={stateBoxStyle}>
                <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
                <span>Carregando catálogo...</span>
              </div>
            ) : erro ? (
              <div style={stateBoxStyle}>
                <span style={{ color: AUTOPILOT_COLORS.statusDanger }}>{erro}</span>
                <Button size="sm" color="info" outline onClick={carregar}>
                  Tentar novamente
                </Button>
              </div>
            ) : items.length ? (
              <>
                <div style={gridStyle}>
                  {items.map((item, indice) => (
                    <React.Fragment key={item.id}>
                      {indice === indicePrimeiroSemCobertura && (
                        <div style={divisorSemCoberturaStyle}>Sem cobertura informada nesta rota</div>
                      )}
                      <CatalogoCard
                        item={item}
                        selecionado={selectedIds.has(item.id)}
                        aberto={selectedDetalheId === item.id}
                        onToggleSelecionado={toggleSelecionado}
                        onAdicionar={adicionarUma}
                        adicionando={adicionandoIds.has(item.id)}
                        onAbrirDetalhe={() => setSelectedDetalheId(item.id)}
                      />
                    </React.Fragment>
                  ))}
                </div>

                {page * PAGE_SIZE < total && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Button
                      color="info"
                      outline
                      disabled={loading}
                      onClick={() => {
                        setPage(page + 1);
                        if (listaRef.current) listaRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                    >
                      {`Ver mais transportadoras (página ${page + 1} de ${Math.ceil(total / PAGE_SIZE)})`}
                    </Button>
                  </div>
                )}

                <TablePagination
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  itemLabel="transportadora(s)"
                  showPageSize={false}
                  disabled={loading}
                  onPageChange={setPage}
                />
              </>
            ) : (
              <div style={stateBoxStyle}>
                <span>
                  {temFiltroOuBusca
                    ? "Nenhuma transportadora encontrada com esses filtros."
                    : "Nenhuma transportadora no catálogo."}
                </span>
                {temFiltroOuBusca && (
                  <Button size="sm" color="info" outline onClick={limparFiltros}>
                    Limpar busca e filtros
                  </Button>
                )}
              </div>
            )}

            <div
              style={{
                marginTop: "1.25rem",
                paddingTop: "0.9rem",
                borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
                fontSize: "0.82rem",
                color: AUTOPILOT_COLORS.muted,
                textAlign: "center",
              }}
            >
              Não encontrou a transportadora que precisa?{" "}
              <a href={mailtoSolicitacao} style={{ color: AUTOPILOT_COLORS.accent }}>
                Solicite ao suporte
              </a>
            </div>
      </ShellCard>

      <DetalheTransportadoraModal
        transportadoraId={selectedDetalheId}
        embarcadorId={embarcadorId}
        onClose={() => setSelectedDetalheId(null)}
        onChanged={carregar}
      />

      <Modal isOpen={importModalOpen} toggle={fecharImportModal} modalClassName="modal-align-top" fade={false}>
        <ModalHeader toggle={fecharImportModal}>Importar transportadoras por planilha</ModalHeader>
        <ModalBody>
          <p style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem" }}>
            Envie uma planilha (.xlsx) com uma coluna <strong style={{ color: AUTOPILOT_COLORS.title }}>cnpj</strong>. Cada CNPJ entra
            na sua carteira pela matriz (0001): quem já está na Rede é vinculado; quem é novo é cadastrado pela Receita e passa pela homologação da Everlog.{" "}
            <a
              href="#modelo"
              onClick={(e) => {
                e.preventDefault();
                baixarModelo();
              }}
              style={{ color: AUTOPILOT_COLORS.accent }}
            >
              Baixar modelo
            </a>
          </p>
          <Input type="file" accept=".xlsx,.xls" bsSize="sm" onChange={(e) => setImportFile(e.target.files && e.target.files[0])} />

          {resultadoImport && (
            <div style={{ marginTop: "1rem", fontSize: "0.82rem", color: AUTOPILOT_COLORS.text }}>
              <div>Total na planilha: {resultadoImport.total}</div>
              <div>Adicionadas: {resultadoImport.vinculadas}</div>
              <div>Já estavam na carteira: {resultadoImport.jaVinculadas}</div>
              {Number(resultadoImport.curadoria) > 0 && (
                <div style={{ color: AUTOPILOT_COLORS.statusWarning }}>Aguardando a Everlog (ajuste de matriz): {resultadoImport.curadoria}</div>
              )}
              {Array.isArray(resultadoImport.itens) && resultadoImport.itens.length > 0 && (
                <div style={{ marginTop: "0.5rem" }}>
                  <div style={{ color: AUTOPILOT_COLORS.muted }}>Esteira por transportadora:</div>
                  <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                    {resultadoImport.itens.slice(0, 20).map((it, i) => (
                      <li key={`${it.cnpj}-${i}`}>
                        {it.cnpj} — {it.estado === "ERRO" ? it.motivo || "erro" : FASE_ROTULO[it.estado] || it.estado || "—"}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(resultadoImport.naoEncontradas || []).length > 0 && (
                <div style={{ marginTop: "0.5rem" }}>
                  <div style={{ color: AUTOPILOT_COLORS.statusWarning }}>{resultadoImport.naoEncontradas.length} não encontrada(s):</div>
                  <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                    {resultadoImport.naoEncontradas.slice(0, 10).map((n, i) => (
                      <li key={`${n.cnpj}-${i}`}>
                        {n.cnpj} — {n.motivo}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" color="secondary" outline onClick={fecharImportModal}>
            Fechar
          </Button>
          <Button size="sm" color="info" disabled={!importFile || importando} onClick={importar}>
            {importando ? <Spinner size="sm" /> : "Importar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
